import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Select,
  Pagination,
  Skeleton,
  Empty,
  message,
  Carousel,
  Tag,
  Typography,
} from 'antd';
import { ShoppingCartOutlined, HeartOutlined, RightOutlined } from '@ant-design/icons';
import { Search } from 'lucide-react';
import {
  productService,
  cartService,
  wishlistService,
  addressService,
  storageUrl,
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import './Products.css';

const { Title, Paragraph } = Typography;

// Neutral placeholder so broken images never show alt text.
const PLACEHOLDER = 'https://placehold.co/600x450/eef2f7/94a3b8?text=Current+Care';

// Nepali-style price: "Rs 1,09,800" (lakh grouping).
const rs = (n) => 'Rs ' + Number(n || 0).toLocaleString('en-IN');
// `sale_price` is the discounted selling price (null when no discount);
// the API's plain `price` is the marked price to strike through.
const marketPrice = (p) =>
  p.sale_price != null && Number(p.sale_price) < Number(p.price) ? Number(p.price) : null;

const categories = [
  { value: 'all', label: 'All Products' },
  { value: 'electrical-appliances', label: 'Electrical Appliances' },
  { value: 'consumer-electronics', label: 'Consumer Electronics' },
  { value: 'electrical-components', label: 'Electrical Components' },
];

const sortOptions = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'distance', label: 'Nearest to you' },
  { value: 'price', label: 'Price (low to high)' },
  { value: 'rating', label: 'Top rated' },
];

const getCategorySlug = (categoryName) => {
  if (!categoryName) return 'other';
  return categoryName.toLowerCase().replace(/\s+/g, '-');
};

const Products = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [coords, setCoords] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Recommend nearest providers first using the customer's default address.
    const token = localStorage.getItem('authToken');
    if (!token) return;
    addressService
      .getAddresses()
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        const withCoords = list.filter((a) => a.latitude && a.longitude);
        if (withCoords.length === 0) return;
        const primary = list.find((a) => a.is_default) || withCoords[0];
        setCoords({ lat: primary.latitude, lng: primary.longitude });
      })
      .catch(() => {});
  }, []);

  const requireAuth = (callback) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      callback();
    } else {
      message.info('Please login or register to continue');
      navigate('/login', { state: { from: window.location.pathname } });
    }
  };

  const handleAddToCart = async (product) => {
    try {
      await cartService.addToCart(product.id);
      message.success(`${product.name} added to cart!`);
    } catch (error) {
      message.error('Failed to add to cart');
    }
  };

  const handleSave = async (product) => {
    try {
      await wishlistService.addToWishlist(product.id);
      message.success(`${product.name} saved!`);
    } catch (error) {
      message.error('Failed to save to wishlist');
    }
  };

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const params = { limit: 40, type: 'product' };
        if (coords) {
          params.lat = coords.lat;
          params.lng = coords.lng;
          params.sort = 'distance';
        }
        const [productsRes, bannerRes] = await Promise.all([
          productService.getProducts(params),
          productService.getBanners(),
        ]);
        const productsData = productsRes?.data?.data || productsRes?.data || [];
        if (Array.isArray(productsData)) {
          setProducts(
            productsData.map((product) => ({
              ...product,
              image:
                storageUrl(product.images?.[0]?.url || product.images?.[0]?.image_url) ||
                PLACEHOLDER,
              brand: product.brand || product.category || '',
              desc: product.desc || product.description || product.short_description || '',
              categorySlug: getCategorySlug(product.category),
              rating: product.rating || 0,
            })),
          );
        }
        const bannerData = bannerRes?.data || [];
        if (Array.isArray(bannerData)) {
          setBanners(bannerData.filter((b) => b.is_active !== false));
        }
        if (coords) setSortBy('distance');
      } catch (error) {
        console.error('Failed to load products', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [coords]);

  const filteredProducts = products
    .filter((product) => selectedCategory === 'all' || product.categorySlug === selectedCategory)
    .filter(
      (product) =>
        !searchQuery.trim() ||
        (product.name || '').toLowerCase().includes(searchQuery.trim().toLowerCase()),
    )
    .sort((a, b) => {
      if (sortBy === 'distance') {
        const ad = a.distance_km != null ? Number(a.distance_km) : Infinity;
        const bd = b.distance_km != null ? Number(b.distance_km) : Infinity;
        return ad - bd;
      }
      if (sortBy === 'price') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return (a.name || '').localeCompare(b.name || '');
    });

  const pageSize = 12;
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const onCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };
  const onSortChange = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  return (
    <div className="products-page">
      {/* Compact page header band */}
      <div className="products-hero">
        <div className="products-hero-inner">
          <span className="products-eyebrow">Shop</span>
          <h1 className="products-heading">Products</h1>
          <p className="products-subtitle">
            Genuine appliances, electronics and components from trusted brands, delivered across
            Nepal.
          </p>
          <div className="cat-search">
            <Input
              size="large"
              allowClear
              placeholder="Search products..."
              prefix={<Search size={17} style={{ color: '#94a3b8' }} />}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Banner Carousel */}
      {(() => {
        const productBanners = banners.filter((b) =>
          ['home_top', 'new_arrivals', 'promotion'].includes(b.banner_type),
        );
        if (productBanners.length === 0) return null;
        return (
          <section className="home-section">
            <div style={{ marginBottom: 24, borderRadius: 16, overflow: 'hidden' }}>
              <Carousel autoplay autoplaySpeed={5000} dotPosition="bottom">
                {productBanners.map((banner, i) => (
                  <div key={banner.id || i}>
                    <div
                      onClick={() => banner.target_url && window.open(banner.target_url, '_blank')}
                      style={{
                        height: 240,
                        borderRadius: 16,
                        background: `linear-gradient(135deg, ${['#1a3a2a', '#2d5a3d', '#1e4d3a', '#3d7a4d'][i % 4]}, ${['#52b788', '#3d7a4d', '#2d5a3d', '#7fb069'][i % 4]})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        cursor: banner.target_url ? 'pointer' : 'default',
                        overflow: 'hidden',
                      }}
                    >
                      {banner.image_url && (
                        <img
                          src={storageUrl(banner.image_url)}
                          alt={banner.title}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: 0.3,
                          }}
                        />
                      )}
                      <div
                        style={{
                          position: 'relative',
                          zIndex: 1,
                          textAlign: 'center',
                          padding: '0 40px',
                          maxWidth: 700,
                        }}
                      >
                        <Tag
                          color={['green', 'blue', 'gold', 'purple'][i % 4]}
                          style={{
                            marginBottom: 12,
                            fontSize: 13,
                            padding: '4px 16px',
                            borderRadius: 20,
                          }}
                        >
                          {banner.banner_type?.replace('_', ' ').toUpperCase() || 'FEATURED'}
                        </Tag>
                        <Title level={2} style={{ color: '#fff', margin: 0, fontSize: 32 }}>
                          {banner.title}
                        </Title>
                        {banner.description && (
                          <Paragraph
                            style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, marginTop: 8 }}
                          >
                            {banner.description}
                          </Paragraph>
                        )}
                        {banner.target_url && (
                          <Button
                            type="primary"
                            ghost
                            style={{
                              marginTop: 12,
                              borderColor: '#fff',
                              color: '#fff',
                              borderRadius: 20,
                              height: 40,
                              padding: '0 24px',
                            }}
                            onClick={() => window.open(banner.target_url, '_blank')}
                          >
                            Learn More <RightOutlined />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          </section>
        );
      })()}

      <section className="home-section products-body">
        {/* Filter / sort toolbar */}
        <div className="products-toolbar">
          <div className="products-count">
            {loading ? 'Loading products…' : `${filteredProducts.length} products`}
          </div>
          <div className="products-controls">
            <Select
              value={selectedCategory}
              onChange={onCategoryChange}
              options={categories}
              className="products-select"
              popupMatchSelectWidth={false}
            />
            <Select
              value={sortBy}
              onChange={onSortChange}
              options={sortOptions}
              className="products-select"
              popupMatchSelectWidth={false}
            />
          </div>
        </div>

        {loading ? (
          <Row gutter={[24, 24]}>
            {Array.from({ length: 8 }).map((_, idx) => (
              <Col xs={12} md={8} lg={6} key={idx}>
                <Card className="product-card">
                  <Skeleton.Image active className="products-skeleton-img" />
                  <Skeleton active paragraph={{ rows: 3 }} />
                </Card>
              </Col>
            ))}
          </Row>
        ) : paginatedProducts.length === 0 ? (
          <Empty description="No products found" style={{ padding: '60px 0' }} />
        ) : (
          <Row gutter={[24, 24]}>
            {paginatedProducts.map((product) => (
              <Col xs={12} md={8} lg={6} key={product.id}>
                <Card
                  hoverable
                  className="product-card"
                  cover={
                    <div className="product-cover">
                      <img
                        alt={product.name}
                        src={product.image}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PLACEHOLDER;
                        }}
                      />
                      {product.discount ? (
                        <span className="product-badge">{product.discount}% off</span>
                      ) : null}
                      <button
                        type="button"
                        className="product-wish"
                        aria-label="Add to wishlist"
                        onClick={(e) => {
                          e.stopPropagation();
                          requireAuth(() => handleSave(product));
                        }}
                      >
                        <HeartOutlined />
                      </button>
                    </div>
                  }
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  {product.brand ? <div className="product-brand">{product.brand}</div> : null}
                  {product.distance_km != null ? (
                    <div
                      className="product-distance"
                      style={{ color: '#059669', fontSize: 12, margin: '2px 0 6px' }}
                    >
                      ~{Number(product.distance_km).toFixed(1)} km away
                    </div>
                  ) : null}
                  <div className="product-title">{product.name}</div>
                  {product.desc ? <div className="product-desc">{product.desc}</div> : null}
                  <div className="product-price-row">
                    <span className="product-price">{rs(product.sale_price ?? product.price)}</span>
                    {marketPrice(product) ? (
                      <span className="product-original">{rs(marketPrice(product))}</span>
                    ) : null}
                  </div>
                  <Button
                    type="primary"
                    block
                    className="product-cart-btn"
                    icon={<ShoppingCartOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      requireAuth(() => handleAddToCart(product));
                    }}
                  >
                    Add to Cart
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {!loading && filteredProducts.length > pageSize ? (
          <div className="products-pagination">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredProducts.length}
              showSizeChanger={false}
              onChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default Products;
